import { createElement } from "react";
import toast from "react-hot-toast";

/**
 * notify — the one toast policy (ENG-APP-UX-TUNEUP Track D).
 *
 * Source: docs/product/experience-and-ui/48-in-app-design-spec.md § 2 Toast
 * (ink surface, 8 px radius, max 520 px, one action + close) and Rule 8:
 * a toast is for a reversible act the user did not initiate from a dialog —
 * dismiss, suppress, apply — and it carries Undo with a stated window; it
 * is NOT a status channel ("toasts for everything" was audit F1). Every
 * surface routes through these verbs so the durations and the one action
 * stay uniform; the `Toaster` in `main.tsx` carries the tokens.
 *
 * Durations: success 3 s, error 5 s (long enough to read the fix), undo 10 s
 * (the Undo window 48 § 2.5 states beside it; react-hot-toast pauses it on
 * hover only, so the Undo row that stays on the object is the keyboard
 * path), info 4 s.
 */
export const NOTIFY_DURATION_MS = {
  success: 3000,
  error: 5000,
  undo: 10_000,
  info: 4000,
} as const;

export interface UndoOptions {
  /** The reversal. Called at most once. */
  onUndo: () => void;
  /** The action's label — "Undo" in the catalog's language. */
  undoLabel: string;
  /** A stable id for the act: a re-issue replaces the toast in place
   *  (react-hot-toast upserts by id) and `dismiss(id)` retires it from
   *  anywhere -- the Toaster is global, a caller's instance is not. */
  id?: string;
}

const UNDO_BUTTON_CLASS =
  "ml-3 inline-flex min-h-6 items-center rounded-md px-2 text-sm font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

export const notify = {
  success(message: string): string {
    return toast.success(message, { duration: NOTIFY_DURATION_MS.success });
  },
  error(message: string): string {
    return toast.error(message, { duration: NOTIFY_DURATION_MS.error });
  },
  info(message: string): string {
    return toast(message, { duration: NOTIFY_DURATION_MS.info });
  },
  /**
   * A reversible act: the message and ONE action. The toast closes when
   * Undo is pressed; the window is the toast's own lifetime.
   */
  undo(message: string, { onUndo, undoLabel, id }: UndoOptions): string {
    return toast(
      (instance) =>
        createElement(
          "span",
          { className: "inline-flex items-center" },
          createElement("span", null, message),
          createElement(
            "button",
            {
              type: "button",
              className: UNDO_BUTTON_CLASS,
              onClick: () => {
                toast.dismiss(instance.id);
                onUndo();
              },
            },
            undoLabel,
          ),
        ),
      { duration: NOTIFY_DURATION_MS.undo, id },
    );
  },
  /** Retire ONE toast by id. Never the dismiss-all path: react-hot-toast's
   *  `toast.dismiss(undefined)` hides every toast on screen, so a missing
   *  id is a no-op here (T4 review). */
  dismiss(id: string): void {
    if (id) toast.dismiss(id);
  },
};

export default notify;
